/**
 * BenefitsSubTab Component
 *
 * Manages investment benefits with new schema supporting:
 * - Benefit categories (cost_reduction, revenue_increase, fte_replacement, etc.)
 * - Frequency (one_time, monthly, quarterly, annually)
 * - Specialized calculations for FTE, efficiency, revenue
 * - Qualitative benefits for strategic value
 */

import {
  useBenefits,
  useCreateBenefit,
  useUpdateBenefit,
  useDeleteBenefit,
} from '@medsuite/api-client';
import {
  BenefitCategoryLabel,
  FrequencyLabel,
  QualitativeBenefitTypeLabel,
  QUALITATIVE_BENEFIT_TYPES,
} from '@medsuite/models';
import {
  Button,
  Input,
  TextArea,
  DatePicker,
  Card,
  Modal,
  ModalFooter,
  Select,
  useDialog,
} from '@medsuite/ui-components';
import { useState, useEffect, useRef } from 'react';

import type {
  Investment,
  InvestmentBenefit,
  BenefitCategory,
  Frequency,
  QualitativeBenefitType,
} from '@medsuite/models';

interface BenefitsSubTabProps {
  investment: Investment;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
}

interface LocalBenefit {
  investment_benefit_id?: number;
  benefit_category: BenefitCategory;
  title: string;
  description: string;
  assumptions?: string;

  // Timing
  is_quantifiable: boolean;
  frequency: Frequency;
  start_date: Date | null;
  end_date: Date | null;

  // Amount fields
  amount: number | null;
  amount_type: 'C' | 'O' | 'R' | null;

  // Revenue-specific
  revenue_calc_type?: 'fixed_amount' | 'percentage';
  revenue_percentage?: number | null;
  baseline_revenue?: number | null;

  // FTE/Efficiency-specific
  hourly_rate?: number | null;
  hours_saved_per_week?: number | null;
  fte_count?: number | null;
  annual_salary?: number | null;

  // Qualitative
  qualitative_category?: QualitativeBenefitType | null;
  qualitative_notes?: string;

  // Legacy
  benefit_type?: 'Q' | 'N';
  is_one_time?: boolean;
}

// Benefit category options for Select component
const benefitCategoryOptions: Array<{ value: string; label: string }> = [
  { value: '', label: 'Select category' },
  { value: 'cost_reduction', label: 'Cost Reduction' },
  { value: 'revenue_increase', label: 'Revenue Increase' },
  { value: 'fte_replacement', label: 'FTE Replacement' },
  { value: 'efficiency_gain', label: 'Efficiency Gain' },
  { value: 'cost_avoidance', label: 'Cost Avoidance' },
  { value: 'qualitative', label: 'Qualitative' },
];

// Benefit category descriptions
const BENEFIT_CATEGORY_DESCRIPTIONS: Record<string, string> = {
  cost_reduction: 'Reduced hosting, eliminated tools, lower expenses',
  revenue_increase: 'ARR growth, new revenue streams',
  fte_replacement: 'Headcount reduction or avoided hiring',
  efficiency_gain: 'Process time savings, productivity improvements',
  cost_avoidance: 'Avoided future expense, prevented cost increases',
  qualitative: 'Strategic value, non-quantitative benefits',
};

// Frequency options for Select component
const frequencyOptions: Array<{ value: string; label: string }> = [
  { value: 'one_time', label: 'One-Time' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
];

// Revenue calc type options
const revenueCalcTypeOptions: Array<{ value: string; label: string }> = [
  { value: '', label: 'Select method' },
  { value: 'fixed_amount', label: 'Fixed Amount' },
  { value: 'percentage', label: 'Percentage of Baseline Revenue' },
];

// Amount type options
const amountTypeOptions: Array<{ value: string; label: string }> = [
  { value: '', label: 'Select type' },
  { value: 'C', label: 'CapEx Savings' },
  { value: 'O', label: 'OpEx Savings' },
];

// Qualitative benefit type options for Select component
const qualitativeCategoryOptions: Array<{ value: string; label: string }> = [
  { value: '', label: 'Select type' },
  ...QUALITATIVE_BENEFIT_TYPES.map((type) => ({
    value: type,
    label: QualitativeBenefitTypeLabel[type],
  })),
];

/**
 * Format currency helper
 */
const formatCurrency = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) {
    return '—';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Helper function to convert date to yyyy-MM-dd format for date inputs
 */
const formatDateForInput = (date: Date | string | null): string => {
  if (!date) {
    return '';
  }
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) {
    return '';
  }
  return d.toISOString().split('T')[0] ?? '';
};

/**
 * Calculate monthly benefit amount based on category and frequency
 */
const calculateMonthlyAmount = (benefit: LocalBenefit): number => {
  if (!benefit.is_quantifiable || benefit.benefit_category === 'qualitative') {
    return 0;
  }

  // Handle FTE replacement
  if (benefit.benefit_category === 'fte_replacement') {
    if (benefit.annual_salary && benefit.fte_count) {
      return (Number(benefit.fte_count) * Number(benefit.annual_salary)) / 12;
    }
    if (benefit.hourly_rate && benefit.fte_count) {
      // 2080 hours/year for full FTE
      return (Number(benefit.fte_count) * Number(benefit.hourly_rate) * 2080) / 12;
    }
  }

  // Handle efficiency gain
  if (benefit.benefit_category === 'efficiency_gain') {
    if (benefit.hours_saved_per_week && benefit.hourly_rate) {
      return Number(benefit.hours_saved_per_week) * Number(benefit.hourly_rate) * 4.33;
    }
  }

  // Handle revenue increase
  if (benefit.benefit_category === 'revenue_increase') {
    if (
      benefit.revenue_calc_type === 'percentage' &&
      benefit.revenue_percentage &&
      benefit.baseline_revenue
    ) {
      return ((Number(benefit.revenue_percentage) / 100) * Number(benefit.baseline_revenue)) / 12;
    }
  }

  // Handle standard amount with frequency
  const amount = Number(benefit.amount) || 0;
  const frequency = benefit.frequency || 'monthly';

  switch (frequency) {
    case 'one_time':
      return 0; // One-time benefits are not monthly recurring
    case 'weekly':
      return amount * 4.33;
    case 'monthly':
      return amount;
    case 'quarterly':
      return amount / 3;
    case 'annually':
      return amount / 12;
    default:
      return amount;
  }
};

export default function BenefitsSubTab({
  investment,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isEditing: _isEditing,
  setIsEditing,
}: BenefitsSubTabProps) {
  const { confirm } = useDialog();
  const investmentId = investment?.investment_id ?? 0;
  const isValidId = investmentId > 0 && !isNaN(investmentId);

  // Fetch benefits
  const { data: benefits = [], isLoading } = useBenefits(investmentId, {
    enabled: isValidId,
  });

  // Mutations
  const createBenefit = useCreateBenefit(investmentId);
  const updateBenefit = useUpdateBenefit(investmentId);
  const deleteBenefit = useDeleteBenefit(investmentId);

  // Local state for editing
  const [localBenefits, setLocalBenefits] = useState<LocalBenefit[]>([]);

  // Track previous investment ID and benefits to prevent infinite loops
  const prevInvestmentIdRef = useRef<number | null>(null);
  const prevBenefitsRef = useRef<string>('');

  // Modal state
  const [benefitModalOpen, setBenefitModalOpen] = useState(false);
  const [editingBenefitIndex, setEditingBenefitIndex] = useState<number | null>(null);
  const [benefitForm, setBenefitForm] = useState<Partial<LocalBenefit>>({});
  const [isSavingBenefit, setIsSavingBenefit] = useState(false);

  // Sync with fetched data
  useEffect(() => {
    if (benefits) {
      const benefitsJson = JSON.stringify(benefits);

      // Only update if investment changed or benefits data changed
      if (
        prevInvestmentIdRef.current !== investmentId ||
        prevBenefitsRef.current !== benefitsJson
      ) {
        const mappedBenefits: LocalBenefit[] = benefits.map((b: InvestmentBenefit) => ({
          investment_benefit_id: b.investment_benefit_id,
          benefit_category:
            b.benefit_category || (b.benefit_type === 'N' ? 'qualitative' : 'cost_reduction'),
          title: b.title || '',
          description: b.description || '',
          assumptions: b.assumptions || '',
          is_quantifiable: b.is_quantifiable !== false && b.benefit_category !== 'qualitative',
          frequency: b.frequency || (b.is_one_time ? 'one_time' : 'monthly'),
          start_date: b.start_date ? new Date(b.start_date) : null,
          end_date: b.end_date ? new Date(b.end_date) : null,
          amount: b.amount,
          amount_type: b.amount_type || null,
          revenue_calc_type: b.revenue_calc_type || undefined,
          revenue_percentage: b.revenue_percentage,
          baseline_revenue: b.baseline_revenue,
          hourly_rate: b.hourly_rate,
          hours_saved_per_week: b.hours_saved_per_week,
          fte_count: b.fte_count,
          annual_salary: b.annual_salary,
          qualitative_category: b.qualitative_category || null,
          qualitative_notes: b.qualitative_notes || '',
          // Legacy
          benefit_type: b.benefit_type,
          is_one_time: b.is_one_time ?? undefined,
        }));
        setLocalBenefits(mappedBenefits);
        prevInvestmentIdRef.current = investmentId;
        prevBenefitsRef.current = benefitsJson;
      }
    }
  }, [investmentId, benefits]);

  // Separate benefits by quantitative vs qualitative
  const quantifiableBenefits = localBenefits.filter(
    (b) => b.is_quantifiable && b.benefit_category !== 'qualitative'
  );
  const qualitativeBenefits = localBenefits.filter(
    (b) => !b.is_quantifiable || b.benefit_category === 'qualitative'
  );

  // Calculate totals
  const annualTotal = quantifiableBenefits.reduce((sum, b) => {
    const monthly = calculateMonthlyAmount(b);
    if (b.frequency === 'one_time') {
      return sum + (Number(b.amount) || 0);
    }
    return sum + monthly * 12;
  }, 0);

  // Handlers
  const handleOpenBenefitModal = (isQuantifiable: boolean, index?: number) => {
    if (index !== undefined && index >= 0) {
      // Edit existing
      const benefitsList = isQuantifiable ? quantifiableBenefits : qualitativeBenefits;
      const benefit = benefitsList[index];

      if (!benefit) {
        return;
      }

      const globalIndex = localBenefits.indexOf(benefit);
      setEditingBenefitIndex(globalIndex);

      setBenefitForm({
        ...benefit,
      });
    } else {
      // Add new
      setEditingBenefitIndex(null);
      setBenefitForm({
        benefit_category: isQuantifiable ? 'cost_reduction' : 'qualitative',
        title: '',
        description: '',
        assumptions: '',
        is_quantifiable: isQuantifiable,
        frequency: 'monthly',
        start_date: null,
        end_date: null,
        amount: null,
        amount_type: null,
        revenue_calc_type: undefined,
        revenue_percentage: null,
        baseline_revenue: null,
        hourly_rate: null,
        hours_saved_per_week: null,
        fte_count: null,
        annual_salary: null,
        qualitative_category: null,
        qualitative_notes: '',
      });
    }
    setBenefitModalOpen(true);
  };

  const handleCloseBenefitModal = () => {
    setBenefitModalOpen(false);
    setEditingBenefitIndex(null);
    setBenefitForm({});
    setIsSavingBenefit(false);
  };

  const handleSaveBenefit = async () => {
    // Guard: Prevent saving with invalid investment ID
    if (!isValidId) {
      console.error('Cannot save benefit: invalid investment ID');
      return;
    }

    setIsSavingBenefit(true);

    try {
      // Convert dates to ISO strings for the API
      const startDateStr =
        benefitForm.start_date instanceof Date
          ? benefitForm.start_date.toISOString()
          : benefitForm.start_date || null;
      const endDateStr =
        benefitForm.end_date instanceof Date
          ? benefitForm.end_date.toISOString()
          : benefitForm.end_date || null;

      // Build the payload
      const payload = {
        benefit_category: benefitForm.benefit_category,
        title: benefitForm.title || '',
        description: benefitForm.description || undefined,
        assumptions: benefitForm.assumptions || undefined,
        is_quantifiable: benefitForm.is_quantifiable,
        frequency: benefitForm.frequency,
        start_date: startDateStr || undefined,
        end_date: endDateStr || undefined,
        amount: benefitForm.amount != null ? Number(benefitForm.amount) : undefined,
        amount_type:
          benefitForm.benefit_category === 'revenue_increase'
            ? 'R'
            : benefitForm.amount_type || undefined,
        // Revenue fields
        revenue_calc_type:
          benefitForm.benefit_category === 'revenue_increase'
            ? benefitForm.revenue_calc_type
            : undefined,
        revenue_percentage:
          benefitForm.benefit_category === 'revenue_increase' &&
          benefitForm.revenue_percentage != null
            ? Number(benefitForm.revenue_percentage)
            : undefined,
        baseline_revenue:
          benefitForm.benefit_category === 'revenue_increase' &&
          benefitForm.baseline_revenue != null
            ? Number(benefitForm.baseline_revenue)
            : undefined,
        // FTE fields
        fte_count:
          benefitForm.benefit_category === 'fte_replacement' && benefitForm.fte_count != null
            ? Number(benefitForm.fte_count)
            : undefined,
        annual_salary:
          benefitForm.benefit_category === 'fte_replacement' && benefitForm.annual_salary != null
            ? Number(benefitForm.annual_salary)
            : undefined,
        // Efficiency / FTE hourly rate
        hourly_rate:
          (benefitForm.benefit_category === 'efficiency_gain' ||
            benefitForm.benefit_category === 'fte_replacement') &&
          benefitForm.hourly_rate != null
            ? Number(benefitForm.hourly_rate)
            : undefined,
        hours_saved_per_week:
          benefitForm.benefit_category === 'efficiency_gain' &&
          benefitForm.hours_saved_per_week != null
            ? Number(benefitForm.hours_saved_per_week)
            : undefined,
        // Qualitative fields
        qualitative_category:
          benefitForm.benefit_category === 'qualitative' || !benefitForm.is_quantifiable
            ? benefitForm.qualitative_category || undefined
            : undefined,
        qualitative_notes:
          benefitForm.benefit_category === 'qualitative' || !benefitForm.is_quantifiable
            ? benefitForm.qualitative_notes || undefined
            : undefined,
        // Legacy mapping for backward compatibility
        benefit_type: benefitForm.is_quantifiable ? ('Q' as const) : ('N' as const),
        is_one_time: benefitForm.frequency === 'one_time',
      };

      if (editingBenefitIndex !== null) {
        // Update existing
        const benefit = localBenefits[editingBenefitIndex];
        if (benefit?.investment_benefit_id) {
          await updateBenefit.mutateAsync({
            benefitId: benefit.investment_benefit_id,
            data: payload,
          });
        }
      } else {
        // Add new
        await createBenefit.mutateAsync(payload);
      }
      setIsEditing(true);
      handleCloseBenefitModal();
    } catch (error) {
      console.error('Failed to save benefit:', error);
      setIsSavingBenefit(false);
    }
  };

  const handleDeleteBenefit = async (isQuantifiable: boolean, index: number) => {
    const benefitsList = isQuantifiable ? quantifiableBenefits : qualitativeBenefits;
    const benefit = benefitsList[index];

    if (!benefit) {
      return;
    }

    if (benefit.investment_benefit_id) {
      const confirmed = await confirm({
        title: 'Delete Benefit',
        message: 'Are you sure you want to delete this benefit? This action cannot be undone.',
        variant: 'danger',
        confirmText: 'Delete',
      });

      if (!confirmed) {
        return;
      }

      try {
        await deleteBenefit.mutateAsync(benefit.investment_benefit_id);
      } catch (error) {
        console.error('Failed to delete benefit:', error);
        return;
      }
    }

    const globalIndex = localBenefits.indexOf(benefit);
    setLocalBenefits(localBenefits.filter((_, i) => i !== globalIndex));
    setIsEditing(true);
  };

  // Calculate displayed monthly value for form preview
  const formMonthlyPreview = calculateMonthlyAmount(benefitForm as LocalBenefit);

  // Render benefit modal
  const renderBenefitModal = () => {
    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      handleSaveBenefit();
    };

    const category = benefitForm.benefit_category;
    const isQuantifiable = benefitForm.is_quantifiable;
    const showRevenueFields = category === 'revenue_increase';
    const showFTEFields = category === 'fte_replacement';
    const showEfficiencyFields = category === 'efficiency_gain';
    const showStandardAmount = category === 'cost_reduction' || category === 'cost_avoidance';
    const showQualitativeFields = category === 'qualitative' || !isQuantifiable;

    return (
      <Modal
        isOpen={benefitModalOpen}
        onClose={handleCloseBenefitModal}
        title={editingBenefitIndex !== null ? 'Edit Benefit' : 'Add Benefit'}
        size="lg"
      >
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto px-1">
            {/* Benefit Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Benefit Category *
              </label>
              <Select
                options={benefitCategoryOptions}
                value={benefitForm.benefit_category || ''}
                onChange={(value) => {
                  const newCategory = value as BenefitCategory;
                  const newIsQuantifiable = newCategory !== 'qualitative';
                  setBenefitForm({
                    ...benefitForm,
                    benefit_category: newCategory,
                    is_quantifiable: newIsQuantifiable,
                    // Auto-set amount_type for revenue benefits
                    amount_type: newCategory === 'revenue_increase' ? 'R' : benefitForm.amount_type,
                    // Reset category-specific fields
                    revenue_calc_type: undefined,
                    revenue_percentage: null,
                    baseline_revenue: null,
                    fte_count: null,
                    annual_salary: null,
                    hourly_rate: null,
                    hours_saved_per_week: null,
                  });
                }}
              />
              {category && BENEFIT_CATEGORY_DESCRIPTIONS[category] && (
                <p className="text-xs text-gray-500 mt-1">
                  {BENEFIT_CATEGORY_DESCRIPTIONS[category]}
                </p>
              )}
            </div>

            {/* Benefit Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Benefit Title *
              </label>
              <Input
                value={benefitForm.title || ''}
                onChange={(e) => setBenefitForm({ ...benefitForm, title: e.target.value })}
                placeholder="Enter benefit title"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <TextArea
                value={benefitForm.description || ''}
                onChange={(e) => setBenefitForm({ ...benefitForm, description: e.target.value })}
                placeholder="Enter benefit description"
                rows={2}
              />
            </div>

            {/* Quantitative benefit fields */}
            {isQuantifiable && category !== 'qualitative' && (
              <>
                {/* Frequency */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Frequency *
                  </label>
                  <Select
                    options={frequencyOptions}
                    value={benefitForm.frequency || 'monthly'}
                    onChange={(value) =>
                      setBenefitForm({ ...benefitForm, frequency: value as Frequency })
                    }
                  />
                </div>

                {/* Date Range */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Start Date
                    </label>
                    <DatePicker
                      value={formatDateForInput(benefitForm.start_date || null)}
                      onChange={(e) =>
                        setBenefitForm({
                          ...benefitForm,
                          start_date: e.target.value ? new Date(e.target.value) : null,
                        })
                      }
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {benefitForm.frequency === 'one_time'
                        ? 'When benefit occurs'
                        : 'When benefit begins'}
                    </p>
                  </div>

                  {benefitForm.frequency !== 'one_time' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        End Date
                      </label>
                      <DatePicker
                        value={formatDateForInput(benefitForm.end_date || null)}
                        onChange={(e) =>
                          setBenefitForm({
                            ...benefitForm,
                            end_date: e.target.value ? new Date(e.target.value) : null,
                          })
                        }
                      />
                      <p className="text-xs text-gray-500 mt-1">Leave empty for perpetual</p>
                    </div>
                  )}
                </div>

                {/* Revenue Increase fields */}
                {showRevenueFields && (
                  <div className="border-t pt-4 mt-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Revenue Calculation
                    </h4>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Calculation Method
                      </label>
                      <Select
                        options={revenueCalcTypeOptions}
                        value={benefitForm.revenue_calc_type || ''}
                        onChange={(value) =>
                          setBenefitForm({
                            ...benefitForm,
                            revenue_calc_type:
                              (value as 'fixed_amount' | 'percentage') || undefined,
                          })
                        }
                      />
                    </div>

                    {benefitForm.revenue_calc_type === 'percentage' && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Revenue Percentage (%)
                          </label>
                          <Input
                            type="number"
                            value={benefitForm.revenue_percentage || ''}
                            onChange={(e) =>
                              setBenefitForm({
                                ...benefitForm,
                                revenue_percentage: parseFloat(e.target.value) || null,
                              })
                            }
                            placeholder="e.g., 5 for 5%"
                            min="0"
                            max="100"
                            step="0.01"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Baseline Annual Revenue ($)
                          </label>
                          <Input
                            type="number"
                            value={benefitForm.baseline_revenue || ''}
                            onChange={(e) =>
                              setBenefitForm({
                                ...benefitForm,
                                baseline_revenue: parseFloat(e.target.value) || null,
                              })
                            }
                            placeholder="e.g., 1000000"
                            min="0"
                            step="0.01"
                          />
                        </div>
                      </div>
                    )}

                    {benefitForm.revenue_calc_type === 'fixed_amount' && (
                      <div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Amount ($)
                          </label>
                          <Input
                            type="number"
                            value={benefitForm.amount || ''}
                            onChange={(e) =>
                              setBenefitForm({
                                ...benefitForm,
                                amount: parseFloat(e.target.value) || null,
                              })
                            }
                            placeholder="0"
                            min="0"
                            step="0.01"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Amount per {benefitForm.frequency || 'period'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* FTE Replacement fields */}
                {showFTEFields && (
                  <div className="border-t pt-4 mt-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      FTE Replacement Calculation
                    </h4>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        FTE Count
                      </label>
                      <Input
                        type="number"
                        value={benefitForm.fte_count || ''}
                        onChange={(e) =>
                          setBenefitForm({
                            ...benefitForm,
                            fte_count: parseFloat(e.target.value) || null,
                          })
                        }
                        placeholder="e.g., 1.5 for 1.5 FTEs"
                        min="0"
                        step="0.1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Number of full-time equivalents (can be fractional)
                      </p>
                    </div>

                    <p className="text-sm text-gray-500 my-2">
                      Enter either annual salary OR hourly rate:
                    </p>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Annual Salary per FTE ($)
                      </label>
                      <Input
                        type="number"
                        value={benefitForm.annual_salary || ''}
                        onChange={(e) =>
                          setBenefitForm({
                            ...benefitForm,
                            annual_salary: parseFloat(e.target.value) || null,
                            hourly_rate: null, // Clear hourly if setting salary
                          })
                        }
                        placeholder="e.g., 75000"
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="text-center text-gray-500 my-2">— or —</div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Hourly Rate ($)
                      </label>
                      <Input
                        type="number"
                        value={benefitForm.hourly_rate || ''}
                        onChange={(e) =>
                          setBenefitForm({
                            ...benefitForm,
                            hourly_rate: parseFloat(e.target.value) || null,
                            annual_salary: null, // Clear salary if setting hourly
                          })
                        }
                        placeholder="e.g., 50"
                        min="0"
                        step="0.01"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Will use 2,080 hours/year for full FTE
                      </p>
                    </div>
                  </div>
                )}

                {/* Efficiency Gain fields */}
                {showEfficiencyFields && (
                  <div className="border-t pt-4 mt-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Efficiency Calculation
                    </h4>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Hours Saved Per Week
                        </label>
                        <Input
                          type="number"
                          value={benefitForm.hours_saved_per_week || ''}
                          onChange={(e) =>
                            setBenefitForm({
                              ...benefitForm,
                              hours_saved_per_week: parseFloat(e.target.value) || null,
                            })
                          }
                          placeholder="e.g., 10"
                          min="0"
                          step="0.5"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Hourly Rate ($)
                        </label>
                        <Input
                          type="number"
                          value={benefitForm.hourly_rate || ''}
                          onChange={(e) =>
                            setBenefitForm({
                              ...benefitForm,
                              hourly_rate: parseFloat(e.target.value) || null,
                            })
                          }
                          placeholder="e.g., 50"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Standard Amount fields (Cost Reduction / Cost Avoidance) */}
                {showStandardAmount && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Amount ($)
                      </label>
                      <Input
                        type="number"
                        value={benefitForm.amount || ''}
                        onChange={(e) =>
                          setBenefitForm({
                            ...benefitForm,
                            amount: parseFloat(e.target.value) || null,
                          })
                        }
                        placeholder="0"
                        min="0"
                        step="0.01"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Amount per {benefitForm.frequency || 'period'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Amount Type
                      </label>
                      <Select
                        options={amountTypeOptions}
                        value={benefitForm.amount_type || ''}
                        onChange={(value) =>
                          setBenefitForm({
                            ...benefitForm,
                            amount_type: (value as 'C' | 'O') || null,
                          })
                        }
                      />
                    </div>
                  </div>
                )}

                {/* Monthly Preview */}
                {formMonthlyPreview > 0 && (
                  <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-md">
                    <p className="text-sm text-green-700 dark:text-green-300">
                      <strong>Calculated Monthly Benefit:</strong>{' '}
                      {formatCurrency(formMonthlyPreview)}
                      {benefitForm.benefit_category === 'efficiency_gain' &&
                        benefitForm.hours_saved_per_week &&
                        benefitForm.hourly_rate && (
                          <span className="block text-xs mt-1">
                            ({benefitForm.hours_saved_per_week} hrs/week × $
                            {benefitForm.hourly_rate}/hr × 4.33 weeks/month)
                          </span>
                        )}
                    </p>
                  </div>
                )}

                {/* Assumptions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Assumptions
                  </label>
                  <TextArea
                    value={benefitForm.assumptions || ''}
                    onChange={(e) =>
                      setBenefitForm({ ...benefitForm, assumptions: e.target.value })
                    }
                    placeholder="Document any assumptions used in the benefit calculation"
                    rows={2}
                  />
                </div>
              </>
            )}

            {/* Qualitative benefit fields */}
            {showQualitativeFields && (
              <div className="border-t pt-4 mt-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Qualitative Benefit Details
                </h4>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Benefit Type
                  </label>
                  <Select
                    options={qualitativeCategoryOptions}
                    value={benefitForm.qualitative_category || ''}
                    onChange={(value) =>
                      setBenefitForm({
                        ...benefitForm,
                        qualitative_category: (value as QualitativeBenefitType) || null,
                      })
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Notes
                  </label>
                  <TextArea
                    value={benefitForm.qualitative_notes || ''}
                    onChange={(e) =>
                      setBenefitForm({ ...benefitForm, qualitative_notes: e.target.value })
                    }
                    placeholder="Describe the strategic value and expected impact"
                    rows={3}
                  />
                </div>
              </div>
            )}
          </div>

          <ModalFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={handleCloseBenefitModal}
              disabled={isSavingBenefit}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSavingBenefit}>
              {isSavingBenefit
                ? 'Saving...'
                : editingBenefitIndex !== null
                  ? 'Save Changes'
                  : 'Add Benefit'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    );
  };

  // Guard: Show loading state if investment ID is invalid
  if (!isValidId) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-gray-500 dark:text-gray-400">Loading investment data...</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-gray-500 dark:text-gray-400">Loading benefits...</div>
      </div>
    );
  }

  return (
    <>
      {renderBenefitModal()}
      <div className="space-y-8 p-6">
        {/* Quantitative Benefits */}
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Quantitative Benefits
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Measurable benefits with financial calculations
                </p>
              </div>
              <Button onClick={() => handleOpenBenefitModal(true)} size="sm">
                + Add Quantitative Benefit
              </Button>
            </div>

            {quantifiableBenefits.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  No quantitative benefits added yet.
                </p>
                <Button onClick={() => handleOpenBenefitModal(true)} variant="outline">
                  Add Your First Benefit
                </Button>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Category
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Title
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Frequency
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Details
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Monthly
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Annual
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {quantifiableBenefits.map((benefit, index) => {
                        const monthly = calculateMonthlyAmount(benefit);
                        const annual =
                          benefit.frequency === 'one_time'
                            ? Number(benefit.amount) || 0
                            : monthly * 12;

                        return (
                          <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                            <td className="px-4 py-4">
                              <span
                                className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  {
                                    cost_reduction:
                                      'text-green-700 bg-green-50 dark:text-green-300 dark:bg-green-900/30',
                                    revenue_increase:
                                      'text-blue-700 bg-blue-50 dark:text-blue-300 dark:bg-blue-900/30',
                                    fte_replacement:
                                      'text-purple-700 bg-purple-50 dark:text-purple-300 dark:bg-purple-900/30',
                                    efficiency_gain:
                                      'text-cyan-700 bg-cyan-50 dark:text-cyan-300 dark:bg-cyan-900/30',
                                    cost_avoidance:
                                      'text-orange-700 bg-orange-50 dark:text-orange-300 dark:bg-orange-900/30',
                                    qualitative:
                                      'text-gray-700 bg-gray-50 dark:text-gray-300 dark:bg-gray-700',
                                  }[benefit.benefit_category] || 'text-gray-700 bg-gray-50'
                                }`}
                              >
                                {BenefitCategoryLabel[benefit.benefit_category] ||
                                  benefit.benefit_category}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                              {benefit.title || '—'}
                            </td>
                            <td className="px-4 py-4">
                              <span
                                className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  {
                                    one_time:
                                      'text-blue-600 bg-blue-50 dark:text-blue-300 dark:bg-blue-900/30',
                                    monthly:
                                      'text-purple-600 bg-purple-50 dark:text-purple-300 dark:bg-purple-900/30',
                                    quarterly:
                                      'text-green-600 bg-green-50 dark:text-green-300 dark:bg-green-900/30',
                                    annually:
                                      'text-orange-600 bg-orange-50 dark:text-orange-300 dark:bg-orange-900/30',
                                    weekly:
                                      'text-cyan-600 bg-cyan-50 dark:text-cyan-300 dark:bg-cyan-900/30',
                                  }[benefit.frequency] || 'text-gray-600 bg-gray-50'
                                }`}
                              >
                                {FrequencyLabel[benefit.frequency] || benefit.frequency}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-xs text-gray-600 dark:text-gray-400">
                              {benefit.benefit_category === 'fte_replacement' &&
                                benefit.fte_count && (
                                  <span>
                                    {benefit.fte_count} FTE @{' '}
                                    {benefit.annual_salary
                                      ? formatCurrency(benefit.annual_salary)
                                      : `$${benefit.hourly_rate}/hr`}
                                  </span>
                                )}
                              {benefit.benefit_category === 'efficiency_gain' &&
                                benefit.hours_saved_per_week && (
                                  <span>
                                    {benefit.hours_saved_per_week} hrs/wk @ ${benefit.hourly_rate}
                                    /hr
                                  </span>
                                )}
                              {benefit.benefit_category === 'revenue_increase' &&
                                benefit.revenue_calc_type === 'percentage' && (
                                  <span>
                                    {benefit.revenue_percentage}% of{' '}
                                    {formatCurrency(benefit.baseline_revenue)}
                                  </span>
                                )}
                              {benefit.benefit_category === 'revenue_increase' &&
                                benefit.revenue_calc_type === 'fixed_amount' && (
                                  <span className="text-gray-500">Revenue</span>
                                )}
                              {(benefit.benefit_category === 'cost_reduction' ||
                                benefit.benefit_category === 'cost_avoidance') && (
                                <span className="text-gray-500">
                                  {benefit.amount_type === 'C'
                                    ? 'CapEx'
                                    : benefit.amount_type === 'O'
                                      ? 'OpEx'
                                      : '—'}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-4 text-sm text-right">
                              {benefit.frequency === 'one_time' ? (
                                <div>
                                  <span className="text-gray-600 dark:text-gray-400 font-mono">
                                    {formatCurrency(benefit.amount)}
                                  </span>
                                  <span className="text-xs text-gray-400 block">one-time</span>
                                </div>
                              ) : (
                                <span className="font-mono text-gray-900 dark:text-gray-100">
                                  {formatCurrency(monthly)}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-4 text-sm font-semibold text-right text-green-600 dark:text-green-400 font-mono">
                              {formatCurrency(annual)}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-right text-sm space-x-2">
                              <Button
                                onClick={() => handleOpenBenefitModal(true, index)}
                                variant="ghost"
                                size="sm"
                                className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
                              >
                                Edit
                              </Button>
                              <Button
                                onClick={() => handleDeleteBenefit(true, index)}
                                variant="ghost"
                                size="sm"
                                className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                              >
                                Delete
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Totals */}
                <div className="mt-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Benefits
                      </div>
                      <div className="mt-1 text-xl font-bold text-gray-900 dark:text-gray-100">
                        {quantifiableBenefits.length} quantitative
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Monthly Total
                      </div>
                      <div className="mt-1 text-xl font-bold text-gray-900 dark:text-gray-100">
                        {formatCurrency(
                          quantifiableBenefits.reduce(
                            (sum, b) => sum + calculateMonthlyAmount(b),
                            0
                          )
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Annual Total
                      </div>
                      <div className="mt-1 text-xl font-bold text-green-600 dark:text-green-400">
                        {formatCurrency(annualTotal)}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </Card>

        {/* Qualitative Benefits */}
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Qualitative Benefits
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Strategic value and non-quantitative benefits
                </p>
              </div>
              <Button onClick={() => handleOpenBenefitModal(false)} size="sm">
                + Add Qualitative Benefit
              </Button>
            </div>

            {qualitativeBenefits.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  No qualitative benefits added yet.
                </p>
                <Button onClick={() => handleOpenBenefitModal(false)} variant="outline">
                  Add Your First Qualitative Benefit
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {qualitativeBenefits.map((benefit, index) => (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-4 py-4 text-sm">
                          {benefit.qualitative_category ? (
                            <span className="px-2 py-0.5 rounded text-xs font-medium text-purple-700 bg-purple-50 dark:text-purple-300 dark:bg-purple-900/30">
                              {QualitativeBenefitTypeLabel[benefit.qualitative_category] ||
                                benefit.qualitative_category}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                          {benefit.title || '—'}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-md">
                          {benefit.description || benefit.qualitative_notes || '—'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right text-sm space-x-2">
                          <Button
                            onClick={() => handleOpenBenefitModal(false, index)}
                            variant="ghost"
                            size="sm"
                            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
                          >
                            Edit
                          </Button>
                          <Button
                            onClick={() => handleDeleteBenefit(false, index)}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Card>
      </div>
    </>
  );
}
